import { defineExporter } from './exportRegistry';
import { CURRENCY_NOT_SET, isCurrencySet } from '../../utils/currency';

/**
 * Exporters of the account-level (workspace) lists. Money columns are left out
 * of the file, header and all, when the person may not see money: the same gate
 * the page uses for its own money columns, handed in as `ctx.showMoney`.
 */
export interface AccountMoneyCtx { showMoney: boolean }

const showMoney = (ctx: AccountMoneyCtx) => ctx.showMoney;
const yesNo = (v: unknown) => (v ? 'Yes' : 'No');
const activeLabel = (archived?: boolean) => (archived ? 'Archived' : 'Active');
const currencyText = (code?: string | null) => (isCurrencySet(code) ? code.trim().toUpperCase() : CURRENCY_NOT_SET);
const sentenceCase = (v?: string | null) => {
  const spaced = (v ?? '').replace(/[_-]+/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : '';
};

interface AccountVendorRow {
  name: string; category: string | null; status: string | null; rating: number | null; ratingCount: number;
  projectName: string; instagram: string | null; archived: boolean;
}
interface AccountClientRow { name: string; email: string | null; phone: string | null; project_count: number; archived: boolean }
interface AccountInvoiceRow {
  invoice_number: string; client_name?: string; status: string; due_date: string | null; currency: string;
  total: number; amount_paid: number; balance_due: number; archived: boolean;
}
interface AccountQuoteRow {
  quote_number: string; client_name?: string; status: string; valid_until: string | null; currency: string;
  total: number; archived: boolean;
}
interface AccountProjectRow {
  project_name: string; project_date: string | null; pipeline_stage: string; role: string; readiness: number;
  contract_value: string | null; contract_currency: string | null;
}
interface AccountCalendarRow {
  day: string; projectName: string; event: { title: string; time?: string; location?: string; category?: string };
}
interface AccountPersonRow {
  name: string; email: string | null; phone: string | null; archived_at: string | null;
  login: { role: string; status: string } | null; projects: { project_name: string }[];
}

export const ACCOUNT_EXPORTERS = {
  'account-vendors': defineExporter<AccountVendorRow>({
    fileName: 'vendor-directory-export.csv',
    columns: [
      { header: 'Vendor', value: v => v.name },
      { header: 'Category', value: v => v.category },
      { header: 'Status', value: v => sentenceCase(v.status) },
      { header: 'Rating', value: v => v.rating },
      { header: 'Reviews', value: v => v.ratingCount },
      { header: 'Project', value: v => v.projectName },
      { header: 'Instagram', value: v => v.instagram },
      { header: 'Archived', value: v => yesNo(v.archived) },
    ],
  }),

  'account-clients': defineExporter<AccountClientRow>({
    fileName: 'clients-export.csv',
    columns: [
      { header: 'Name', value: c => c.name },
      { header: 'Email', value: c => c.email },
      { header: 'Phone', value: c => c.phone },
      { header: 'Projects', value: c => c.project_count },
      { header: 'Status', value: c => activeLabel(c.archived) },
    ],
  }),

  'account-invoices': defineExporter<AccountInvoiceRow, AccountMoneyCtx>({
    fileName: 'invoices-export.csv',
    columns: [
      { header: 'Invoice #', value: i => i.invoice_number },
      { header: 'Client', value: i => i.client_name },
      { header: 'Status', value: i => sentenceCase(i.status) },
      { header: 'Due date', value: i => i.due_date },
      { header: 'Currency', value: i => currencyText(i.currency), when: showMoney },
      { header: 'Total', value: i => i.total, when: showMoney },
      { header: 'Paid', value: i => i.amount_paid, when: showMoney },
      { header: 'Balance', value: i => i.balance_due, when: showMoney },
      { header: 'Archived', value: i => yesNo(i.archived) },
    ],
  }),

  'account-quotes': defineExporter<AccountQuoteRow, AccountMoneyCtx>({
    fileName: 'client-quotes-export.csv',
    columns: [
      { header: 'Quote #', value: q => q.quote_number },
      { header: 'Client', value: q => q.client_name },
      { header: 'Status', value: q => sentenceCase(q.status) },
      { header: 'Valid until', value: q => q.valid_until },
      { header: 'Currency', value: q => currencyText(q.currency), when: showMoney },
      { header: 'Total', value: q => q.total, when: showMoney },
      { header: 'Archived', value: q => yesNo(q.archived) },
    ],
  }),

  'account-projects': defineExporter<AccountProjectRow, AccountMoneyCtx>({
    fileName: 'projects-export.csv',
    columns: [
      { header: 'Project', value: p => p.project_name },
      { header: 'Date', value: p => p.project_date },
      { header: 'Stage', value: p => sentenceCase(p.pipeline_stage) },
      { header: 'Your role', value: p => p.role },
      { header: 'Readiness (%)', value: p => p.readiness },
      { header: 'Contract value', value: p => p.contract_value, when: showMoney },
      { header: 'Currency', value: p => (p.contract_value === null ? '' : currencyText(p.contract_currency)), when: showMoney },
    ],
  }),

  'account-calendar': defineExporter<AccountCalendarRow>({
    fileName: 'calendar-export.csv',
    columns: [
      { header: 'Date', value: e => e.day },
      { header: 'Event', value: e => e.event.title },
      { header: 'Time', value: e => e.event.time },
      { header: 'Location', value: e => e.event.location },
      { header: 'Category', value: e => e.event.category },
      { header: 'Project', value: e => e.projectName },
    ],
  }),

  'account-people': defineExporter<AccountPersonRow>({
    fileName: 'team-and-contacts-export.csv',
    columns: [
      { header: 'Name', value: p => p.name },
      { header: 'Email', value: p => p.email },
      { header: 'Phone', value: p => p.phone },
      { header: 'Login role', value: p => p.login?.role },
      { header: 'Login status', value: p => p.login?.status },
      { header: 'Projects', value: p => p.projects.map(x => x.project_name).join('; ') },
      { header: 'Archived', value: p => yesNo(p.archived_at) },
    ],
  }),
} as const;
